import React, { useState, useEffect } from 'react';
import { AlertCircle, X, Tag } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiBaseUrl } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useAdminToast } from './admin/AdminToast';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';


const LEAD_TYPE_CODES = [
  { value: '1C1V', label: '1C1V — One Customer, One Vehicle' },
  { value: '1CMV', label: '1CMV — One Customer, Multiple Vehicles' },
  { value: 'MCMV', label: 'MCMV — Multiple Customers, Multiple Vehicles' },
  { value: 'MC', label: 'MC — Multiple Customers, No Vehicles' },
  { value: '1C0V', label: '1C0V — One Customer, Zero Vehicles' },
];

const LeadCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showSuccess, toastElement } = useAdminToast();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    active: true
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setError('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/lead-categories`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        setError('Failed to fetch categories');
      }
    } catch (err) {
      setError('Error fetching categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const url = editingCategory
        ? `${getApiBaseUrl()}/admin/lead-categories/${editingCategory.id}`
        : `${getApiBaseUrl()}/admin/lead-categories`;

      const method = editingCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchCategories();
        handleCloseDialog();
        setError('');
        showSuccess(`Category "${formData.name}" ${editingCategory ? 'updated' : 'created'}`);
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to save category');
      }
    } catch (err) {
      setError('Error saving category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setError('');
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      code: category.code,
      active: category.active
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    const category = categories.find((c) => c.id === id);
    const label = category ? category.name : 'this category';
    const confirmed = await confirm({
      title: `Delete lead category "${label}"?`,
      message: `"${label}"${category ? ` (${category.code})` : ''} will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setError('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/lead-categories/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        await fetchCategories();
        setError('');
        showSuccess(`Lead category "${label}" deleted`);
      } else {
        setError('Failed to delete category');
      }
    } catch (err) {
      setError('Error deleting category');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      code: '',
      active: true
    });
    setError('');
  };

  if (loading) return <AdminLoading message="Loading lead categories..." />;

  return (
    <div className="space-y-6">
      {toastElement}
      {confirmDialog}
      <AdminPageHeader
        icon={Tag}
        title="Lead Category Management"
        subtitle="Manage lead categories"
        rightContent={!openDialog && <AdminAddButton onClick={() => { setOpenDialog(true); setError(''); }} label="Add Category" />}
      />

      {error && !openDialog && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.map((category) => (
              <tr key={category.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{category.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{category.code}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <AdminRowActions align="start">
                    <AdminActionButton variant="edit" onClick={() => handleEdit(category)} />
                    <AdminActionButton variant="delete" onClick={() => handleDelete(category.id)} />
                  </AdminRowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h3>
              <button
                onClick={handleCloseDialog}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {error && (
              <div role="alert" className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" />
                <div className="text-sm text-red-800">{error}</div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="mt-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Type (Code)</label>
                <select
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  required
                >
                  <option value="" disabled>Select a lead type...</option>
                  {LEAD_TYPE_CODES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                  {/* Preserve any legacy/unrecognized code already on this category so editing
                      it doesn't silently overwrite it with an unrelated value. */}
                  {formData.code && !LEAD_TYPE_CODES.some((option) => option.value === formData.code) && (
                    <option value={formData.code}>
                      {formData.code} (legacy - not in known lead types)
                    </option>
                  )}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    editingCategory ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadCategoryManagement;
